'use client'

import { useEffect, type RefObject } from 'react'

/** Keep the last frame on screen until the gesture that reached it has ended. */
export function useSequenceEndStop(sectionRef: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    const section = sectionRef.current
    if (!enabled || !section) return
    let held = false
    let released = false
    let resumeWheel = false
    let lastWheel = -Infinity
    let wheelMagnitude = 0
    let heldAt = 0
    let peak = 0
    let trough = 0
    let previousMagnitude = 0
    let risingEvents = 0
    let fallingEvents = 0
    let tailDecayed = false
    let touchY = 0
    let input: 'wheel' | 'touch' | 'key' | null = null
    let previousY = window.scrollY
    const end = () => section.getBoundingClientRect().bottom + window.scrollY - window.innerHeight
    const pin = () => window.scrollTo({ top: end(), behavior: 'instant' })
    const hold = () => {
      if (!held) {
        heldAt = performance.now()
        peak = trough = previousMagnitude = wheelMagnitude
        risingEvents = 0
        fallingEvents = 0
        tailDecayed = false
      }
      held = true
      pin()
    }
    const reset = () => { held = false; released = false; resumeWheel = false; input = null }
    const onPointerDown = (event: PointerEvent) => { if (event.pointerType === 'mouse') reset() }
    const stop = (delta: number, event: Event) => {
      const boundary = end()
      if (delta < 0) { reset(); return }
      if (!delta || released || window.scrollY > boundary + 2) return
      if (held || (window.scrollY >= section.getBoundingClientRect().top + window.scrollY - 1 && window.scrollY + delta >= boundary)) {
        if (event.cancelable) event.preventDefault()
        hold()
      }
    }
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      const now = performance.now()
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1
      const delta = event.deltaY * unit
      wheelMagnitude = Math.abs(delta)
      // macOS momentum can overlap the next finger gesture without a silent gap.
      // Require a decaying tail followed by two rising samples, rather than treating
      // the original swipe's acceleration or a single noisy event as a new gesture.
      let renewedTouchpadGesture = false
      if (held) {
        fallingEvents = wheelMagnitude < previousMagnitude ? fallingEvents + 1 : 0
        peak = Math.max(peak, wheelMagnitude)
        if (fallingEvents >= 2 && wheelMagnitude < peak * .6) tailDecayed = true
        trough = tailDecayed ? Math.min(trough, wheelMagnitude) : wheelMagnitude
        const rising = wheelMagnitude > previousMagnitude && wheelMagnitude > Math.max(trough * 1.8, trough + 2)
        risingEvents = tailDecayed && rising ? risingEvents + 1 : 0
        renewedTouchpadGesture = now - heldAt > 120 && risingEvents >= 2
        previousMagnitude = wheelMagnitude
      }
      const freshBurst = now - lastWheel > 100
      if (freshBurst && !held) resumeWheel = false
      if (held && (freshBurst || renewedTouchpadGesture)) {
        held = false
        released = true
        resumeWheel = true
      }
      lastWheel = now
      input = 'wheel'
      stop(delta, event)
      // A canceled wheel gesture can remain latched in the browser even after
      // preventDefault stops. Explicitly carry the resume gesture into the page.
      if (resumeWheel && !held) {
        if (event.cancelable) event.preventDefault()
        window.scrollBy({ top: delta, behavior: 'instant' })
      }
    }
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) { reset(); return }
      if (held) { held = false; released = true }
      touchY = event.touches[0].clientY
      input = 'touch'
    }
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return
      input = 'touch'
      const nextY = event.touches[0].clientY
      stop(touchY - nextY, event)
      touchY = nextY
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select, button, a, [contenteditable="true"]')) return
      if (!['ArrowDown', 'PageDown', ' '].includes(event.key) || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) { reset(); return }
      if (held && !event.repeat) { held = false; released = true }
      input = 'key'
      stop(event.key === 'ArrowDown' ? 40 : window.innerHeight, event)
    }
    const onScroll = () => {
      const y = window.scrollY
      const boundary = end()
      if (y < boundary - 2) { held = false; released = false }
      // Native touch inertia and smooth wheel scrolling can cross after the input event.
      if (input && !released && y > boundary && previousY <= boundary + 2) hold()
      previousY = window.scrollY
    }
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, { passive: true })
    // Navigation and scrollbar dragging remain native escape routes.
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('resize', reset)
    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('resize', reset)
    }
  }, [enabled, sectionRef])
}
